import { HostName } from "pages/About/hosts";
import { Image } from "shared/types";

export type CategoryTag = string // | string[]

export interface EpisodeResource {
  label: string;
  url?: string;
}

export interface EpisodeResourceGroup {
  tag: string;
  resources: EpisodeResource[]
}

export interface DiscussionPoint {
  title: string;
  description: string;
}

export interface ShowNotes {
  intro: string;
  quote?: {
    text: string;
    attribution: string;
  };
  discussionPoints: DiscussionPoint[];
}

interface EpisodeBase {
  number: string;
  title: string;
  description: string;
  bonus?: boolean;
  titleHighlight?: string;
  resources?: EpisodeResourceGroup[];
  image: Image;
}

export interface UpcomingEpisode extends EpisodeBase {
  upcoming: true;
  host?: HostName;
  category?: CategoryTag;
  publishedDate?: Date;
  showNotes?: ShowNotes;
  audioUrl?: string;
}

export interface PublishedEpisode extends EpisodeBase {
  upcoming?: false;
  host: HostName;
  category: CategoryTag;
  publishedDate: Date;
  showNotes: ShowNotes;
  audioUrl: string;
}

export type Episode = UpcomingEpisode | PublishedEpisode;

export const isUpcomingEpisode = (episode: Episode): episode is UpcomingEpisode =>
  episode.upcoming === true;

export const isPublishedEpisode = (episode: Episode): episode is PublishedEpisode =>
  !episode.upcoming;

export const EPISODES: Episode[] = [
  {
    upcoming: true,
    number: 'easter-bonus',
    host: 'AJ',
    bonus: true,
    category: 'Bonus',
    // publishedDate: new Date(2026,4,6),
    image: {
      src: '/images/episodes/easter-bonus-on-the-hunt-for-the-easter-bunny.png',
      alt: 'A bunny sits in a magical forest surrounded by eggs that are covered in cryptic text'
    },
    title: 'On the Hunt for the Easter Bunny',
    titleHighlight: 'Easter Bunny',
    description:
      'What do bunnies and eggs have to do with Easter? Let\'s explore some Easter traditions and their origins',
    showNotes: {
      intro:
        'Is Easter actually named for the goddess Ishtar? What do bunnies and eggs have to do with it? Let\'s dig into the murky origins of the holiday\'s name, question its pagan roots, and trace how a rabbit hiding eggs somehow became an iconic tradition. All while sharing some of our own favourite childhood Easter memories, and our plans for this year.',
      quote: {
        text: 'It\'s a reminder to be careful what you believe on the internet',
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Where did Easter get its name?',
          description:
            'Online claims of pagan origins attributed to multiple different goddesses, but which one is true?',
        },
        {
          title: "The Easter bunny and its colourful eggs",
          description:
            'What does a bunny have to do with Easter? Why do we give children colourful eggs and chocolate?',
        },
        {
          title: "The Easter... Bilby?",
          description:
            'Australia has chosen to eschew the Easter Bunny in favour of a more local wildlife icon'
        },
        {
          title: 'Easter plans and childhood memories',
          description:
            'We talk about how we\'re making Easter special for our children, while reminiscing about what we loved from our own childhoods.',
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'Easter, Ishtar, Eoster and Eggs',
            url: 'https://historyforatheists.com/2017/04/easter-ishtar-eostre-and-eggs/'
          },
          {
            label: 'Eosturmonath (April)',
            url: 'https://www.carlanayland.org/essays/eosturmonath.htm'
          },
          {
            label: 'On the Bunny Trail: In Search of the Easter Bunny',
            url: 'https://blogs.loc.gov/folklife/2016/03/easter-bunny/'
          },
          {
            label: 'Easter Bilby',
            url: 'https://en.wikipedia.org/wiki/Easter_Bilby'
          },
        ]
      }
    ],
  },
  {
    upcoming: true,
    number: '1',
    host: 'Liz',
    category: 'Cryptids',
    // publishedDate: new Date(2026,4,6),
    image: {
      src: '/images/episodes/1-lets-get-kraken.png',
      alt: 'The Kraken has emerged from the watery depths of the ocean clutching a chest'
    },
    title: 'Let\'s Get Kraken',
    titleHighlight: 'Kraken',
    description:
      'In our inaugural episode, our trio of hero mamas discuss the most legendary monster of the seas, the Kraken.',
    showNotes: {
      intro:
        'What lurks beneath the waves? In our very first episode, we dive into the origins of the Kraken. From twisted Scandinavian folklore to dying giant squids flailing at the surface. We explore how Norse sailors turned real ocean encounters into terrifying legends, and what modern marine biology has to say about it all.',
      quote: {
        text: 'Whales are sea monsters.',
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Origins of the Kraken',
          description:
            'From the Scandinavian word "krake" meaning twisted or sickly, to Norse legends of a colossal squid-like beast that could drag entire ships to the ocean floor.',
        },
        {
          title: 'The Giant Squid Theory',
          description:
            'Marine biologist in.vertebabe explains how dying giant squids fill with ammonia, rise to the surface in a delirious state, and flail into ships creating instant Kraken sightings.',
        },
        {
          title: 'Fishermen\'s Friend or Foe?',
          description:
            'Not every Kraken encounter was a disaster, schools of fish would rise to the surface in its wake, giving brave fishermen a massive catch if they survived.',
        },
        {
          title: 'The Kraken in Pop Culture',
          description:
            'From Pirates of the Caribbean to the NHL\'s Seattle Kraken and their adorable mascot Buoy, the legend lives on across movies, novels, video games, and sports.',
        },
        {
          title: 'Pregnancy Stories',
          description:
            'Three very different journeys into motherhood: meticulously planned, "if it happens, it happens," and a complete surprise. Plus, how we each broke the news to our partners.',
        },
      ],
    },
    resources: [
      {
        tag: 'Watch',
        resources: [
          { label: 'in.vertebabe on TikTok: Giant Squid and the Kraken' },
        ]
      },
      {
        tag: 'Read',
        resources: [
          { label: 'The Kraken in Scandinavian Folklore' },
          { label: 'Scylla and Charybdis in Greek Mythology' },
        ]
      },
    ],
  },
];
